import wood from "../../assets/surfaces/real1.jpg";
import carpet from "../../assets/surfaces/real2.jpg";
import felt from "../../assets/surfaces/real3.jpg";
import tile from "../../assets/surfaces/real4.jpg";
import stone from "../../assets/surfaces/real5.jpg";

/**
 * Selectable backdrops for the "Realistic" preview. Three are real
 * photographed surfaces (a desk, a rug, a felt mat); the rest are
 * CSS-only recipes (layered gradients, no image request) approximating a
 * material rather than photographing one - marble, leather, linen, a flat
 * studio grey. `shadow` is the card's cast-shadow tint on that surface -
 * real contact shadows pick up a little of the ambient surface colour, not
 * pure black, which is a big part of what reads as "photographed" rather
 * than "a PNG pasted on a background".
 */
const DESK_CSS = `
  repeating-linear-gradient(98deg,
    rgba(0,0,0,0.09) 0px, rgba(0,0,0,0.09) 1px, transparent 1px, transparent 3px,
    rgba(255,255,255,0.025) 3px, rgba(255,255,255,0.025) 4px, transparent 4px, transparent 9px),
  radial-gradient(ellipse 70% 55% at 26% 14%, rgba(255,210,150,0.14), transparent 62%),
  linear-gradient(150deg, #5a4c38 0%, #3c3226 48%, #221b13 100%)
`;

const MARBLE_CSS = `
  repeating-linear-gradient(120deg,
    rgba(255,255,255,0.05) 0px, rgba(255,255,255,0.05) 2px, transparent 2px, transparent 40px),
  linear-gradient(18deg, transparent 40%, rgba(110,112,122,0.16) 41%, transparent 43%),
  linear-gradient(102deg, transparent 58%, rgba(110,112,122,0.12) 60%, transparent 63%),
  linear-gradient(70deg, transparent 74%, rgba(255,255,255,0.18) 75%, transparent 77%),
  radial-gradient(ellipse 60% 40% at 30% 70%, rgba(255,255,255,0.1), transparent 60%),
  linear-gradient(160deg, #dfdfe0 0%, #cacace 50%, #b6b6bc 100%)
`;

const LEATHER_CSS = `
  repeating-linear-gradient(35deg,
    rgba(0,0,0,0.06) 0px, rgba(0,0,0,0.06) 1px, transparent 1px, transparent 4px),
  repeating-linear-gradient(-35deg,
    rgba(0,0,0,0.05) 0px, rgba(0,0,0,0.05) 1px, transparent 1px, transparent 4px),
  radial-gradient(ellipse 70% 55% at 28% 18%, rgba(190,130,80,0.18), transparent 60%),
  linear-gradient(150deg, #603c26 0%, #482f1f 50%, #2c1c12 100%)
`;

const LINEN_CSS = `
  repeating-linear-gradient(0deg,
    rgba(0,0,0,0.04) 0px, rgba(0,0,0,0.04) 1px, transparent 1px, transparent 3px),
  repeating-linear-gradient(90deg,
    rgba(255,255,255,0.035) 0px, rgba(255,255,255,0.035) 1px, transparent 1px, transparent 3px),
  radial-gradient(ellipse 65% 50% at 25% 15%, rgba(255,255,255,0.12), transparent 60%),
  linear-gradient(160deg, #dad2bd 0%, #c6bda4 55%, #ab9f85 100%)
`;

const STUDIO_CSS = `
  radial-gradient(circle at 22% 28%, rgba(255,255,255,0.06), transparent 42%),
  radial-gradient(circle at 72% 72%, rgba(0,0,0,0.07), transparent 46%),
  linear-gradient(170deg, #9d9d99 0%, #888882 50%, #6e6e67 100%)
`;

const GLASS_CSS = `
  repeating-linear-gradient(90deg,
    rgba(255,255,255,0.15) 0px, rgba(255,255,255,0.15) 2px, transparent 2px, transparent 12px),
  repeating-linear-gradient(0deg,
    rgba(0,0,0,0.08) 0px, rgba(0,0,0,0.08) 1px, transparent 1px, transparent 8px),
  radial-gradient(circle at 15% 20%, rgba(255,255,255,0.2), transparent 35%),
  radial-gradient(circle at 85% 80%, rgba(0,0,0,0.12), transparent 40%),
  linear-gradient(135deg, #e8e8f0 0%, #d0d0d8 50%, #b8b8c0 100%)
`;

const CERAMIC_CSS = `
  repeating-linear-gradient(45deg,
    rgba(0,0,0,0.04) 0px, rgba(0,0,0,0.04) 1px, transparent 1px, transparent 6px),
  repeating-linear-gradient(-45deg,
    rgba(255,255,255,0.06) 0px, rgba(255,255,255,0.06) 1px, transparent 1px, transparent 6px),
  radial-gradient(ellipse 80% 60% at 25% 25%, rgba(255,255,255,0.15), transparent 55%),
  linear-gradient(110deg, #f5f0eb 0%, #e8e3de 45%, #d8d3ce 100%)
`;

export const SURFACES = [
  { key: "default", label: "Desk", image: null, css: DESK_CSS, shadow: "20, 14, 8" },
  { key: "wood", label: "Wood table", image: wood, shadow: "36, 22, 8" },
  { key: "carpet", label: "Rug", image: carpet, shadow: "24, 16, 22" },
  { key: "felt", label: "Felt mat", image: felt, shadow: "14, 14, 16" },
  { key: "tile", label: "Floor tile", image: tile, shadow: "58, 56, 54" },
  { key: "stone", label: "Polished stone", image: stone, shadow: "16, 18, 18" },
  { key: "marble", label: "Marble", image: null, css: MARBLE_CSS, shadow: "26, 26, 30" },
  { key: "leather", label: "Leather", image: null, css: LEATHER_CSS, shadow: "30, 16, 8" },
  { key: "linen", label: "Linen", image: null, css: LINEN_CSS, shadow: "26, 20, 12" },
  { key: "studio", label: "Studio grey", image: null, css: STUDIO_CSS, shadow: "16, 16, 16" },
  { key: "glass", label: "Glass table", image: null, css: GLASS_CSS, shadow: "22, 20, 14" },
  { key: "ceramic", label: "Ceramic tile", image: null, css: CERAMIC_CSS, shadow: "20, 18, 12" },
];

export function getSurface(key) {
  return SURFACES.find((s) => s.key === key) || SURFACES[0];
}
